import { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Copy, Layers, Plus, Trash2, X } from 'lucide-react';
import { parseAnchor, uid } from './state';
import {
  ROUND_CHOICES, blockFromSetup, cancelUpcoming, editTrainingMax, endProgram, programOverview, setupDefaults,
  setupProblem, startProgram, templateChoices, templateOf, weeksUsePct,
} from './programs';
import { RULE_LABEL, fmtKg, progressionOf, repRangeLabel, repsText } from './targets';
import { loadStep } from './setLog';
import { DraftNumberInput, Segmented } from './inputs';
import { FONT_BODY, FONT_MONO } from './theme';
import { Button, Input } from './ui';

/* ────────────────────────────────────────────────────────────────────────── */
/*  Program sheet: the training block of one exercise                         */
/* ────────────────────────────────────────────────────────────────────────── */

const shortDate = key => parseAnchor(key)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) || key;

const label = t => ({ fontSize: 10, color: t.textFaint, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' });

function SectionTitle({ t, children }) {
  return <div style={{ ...label(t), fontSize: 11, padding: '16px 18px 6px' }}>{children}</div>;
}

/* A target set as a chip: "70×3", "90×3+", or "75%×5" without a training max. */
function SetChip({ t, n, set }) {
  const load = set.weight != null ? fmtKg(set.weight) : set.pct != null ? `${fmtKg(set.pct)}%` : '–';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'baseline', gap: 5, padding: '3px 8px', borderRadius: 6,
      background: t.cardBg, border: `1px solid ${t.cardBorder}`,
    }}>
      <span style={{ fontSize: 9, fontWeight: 700, color: t.textFaint, fontFamily: FONT_MONO }}>{n}</span>
      <span style={{ fontSize: 12, fontFamily: FONT_MONO, fontWeight: 700, color: t.text }}>{load}×{repsText(set)}</span>
    </span>
  );
}

export function SetChips({ t, sets }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {sets.map((s, i) => <SetChip key={i} t={t} n={i + 1} set={s} />)}
    </div>
  );
}

function e1rmLine(stats) {
  if (!stats || !stats.sessions) return 'No sessions yet';
  const n = `${stats.sessions} session${stats.sessions === 1 ? '' : 's'}`;
  if (stats.e1rmStart == null) return n;
  if (stats.sessions === 1 || stats.e1rmEnd === stats.e1rmStart) return `${n} · e1RM ${fmtKg(stats.e1rmStart)}`;
  const d = Math.round((stats.e1rmEnd - stats.e1rmStart) * 10) / 10;
  return `${n} · e1RM ${fmtKg(stats.e1rmStart)} → ${fmtKg(stats.e1rmEnd)} (${d > 0 ? '+' : ''}${fmtKg(d)})`;
}

function Row({ t, title, children, top }) {
  return (
    <div style={{ display: 'flex', alignItems: top ? 'flex-start' : 'center', gap: 10, minHeight: 40 }}>
      <span style={{ ...label(t), width: 88, flexShrink: 0, paddingTop: top ? 12 : 0 }}>{title}</span>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
}

const iconBtn = t => ({
  width: 40, height: 40, flexShrink: 0, border: 'none', background: 'transparent', color: t.textFaint,
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
});

/* Custom block weeks: sets as % of the training max or kg, reps, AMRAP. */
function WeekEditor({ t, weeks, onChange }) {
  const setWeek = (wi, sets) => onChange(weeks.map((w, i) => (i === wi ? { ...w, sets } : w)));
  const setSet = (wi, si, patch) => setWeek(wi, weeks[wi].sets.map((s, i) => {
    if (i !== si) return s;
    const next = { ...s, ...patch };
    Object.keys(next).forEach(k => { if (next[k] === null || next[k] === undefined || next[k] === false) delete next[k]; });
    return next;
  }));
  const relabel = list => list.map((w, i) => ({ ...w, label: `Week ${i + 1}` }));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {weeks.map((w, wi) => (
        <div key={wi} style={{ padding: '6px 10px 10px', borderRadius: 10, background: t.surfaceAlt, border: `1px solid ${t.cardBorder}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ ...label(t), flex: 1 }}>{w.label}</span>
            <button aria-label={`Copy ${w.label}`} title="Copy this week" style={iconBtn(t)}
              onClick={() => onChange(relabel([...weeks.slice(0, wi + 1), JSON.parse(JSON.stringify(w)), ...weeks.slice(wi + 1)]))}>
              <Copy size={14} />
            </button>
            {weeks.length > 1 && (
              <button aria-label={`Remove ${w.label}`} style={{ ...iconBtn(t), color: t.danger }}
                onClick={() => onChange(relabel(weeks.filter((_, i) => i !== wi)))}>
                <Trash2 size={14} />
              </button>
            )}
          </div>
          {w.sets.map((s, si) => {
            const pct = s.weight == null;
            return (
              <div key={si} style={{ display: 'grid', gridTemplateColumns: '14px minmax(0, 1fr) 44px 10px 56px 52px 32px', gap: 4, alignItems: 'center', marginTop: 4 }}>
                <span style={{ fontSize: 10, fontFamily: FONT_MONO, color: t.textFaint, fontWeight: 700 }}>{si + 1}</span>
                <DraftNumberInput t={t} ariaLabel={`Set ${si + 1} ${pct ? 'percent' : 'kg'}`} value={pct ? s.pct ?? null : s.weight}
                  min={0.5} max={pct ? 150 : 1000}
                  onChange={v => setSet(wi, si, pct ? { pct: v } : { weight: v })} />
                <button onClick={() => setSet(wi, si, pct ? { weight: s.pct ?? 0, pct: null } : { pct: s.weight || 75, weight: null })}
                  aria-label="Switch % or kg" style={{
                    height: 40, borderRadius: 8, border: `1px solid ${t.cardBorder}`, background: t.cardBg, cursor: 'pointer',
                    color: t.text, fontFamily: FONT_MONO, fontSize: 12, fontWeight: 700, padding: 0,
                  }}>{pct ? '%TM' : 'kg'}</button>
                <span style={{ fontSize: 12, color: t.textFaint, textAlign: 'center' }}>×</span>
                <DraftNumberInput t={t} integer min={1} max={100} ariaLabel={`Set ${si + 1} reps`} value={s.reps ?? null}
                  onChange={v => setSet(wi, si, { reps: v })} />
                <button aria-pressed={!!s.amrap} onClick={() => setSet(wi, si, { amrap: !s.amrap })} title="As many reps as possible" style={{
                  height: 40, borderRadius: 8, cursor: 'pointer', padding: 0, fontFamily: FONT_MONO, fontSize: 11, fontWeight: 700,
                  border: `1px solid ${s.amrap ? t.accentBorder : t.cardBorder}`, background: s.amrap ? t.accentSoft : t.cardBg,
                  color: s.amrap ? t.accent : t.textMuted,
                }}>AMRAP</button>
                <button aria-label={`Remove set ${si + 1}`} style={{ ...iconBtn(t), width: 32 }}
                  onClick={() => setWeek(wi, w.sets.filter((_, i) => i !== si))}>
                  <X size={14} />
                </button>
              </div>
            );
          })}
          <Button t={t} size="sm" variant="ghost" style={{ height: 40, marginTop: 4 }}
            onClick={() => {
              const { amrap, ...last } = w.sets[w.sets.length - 1] || { pct: 75, reps: 5 };
              setWeek(wi, [...w.sets, last]);
            }}>
            <Plus size={13} /> Set
          </Button>
        </div>
      ))}
      <Button t={t} size="sm" style={{ height: 40, alignSelf: 'flex-start' }}
        onClick={() => onChange(relabel([...weeks, JSON.parse(JSON.stringify(weeks[weeks.length - 1] || { sets: [] }))]))}>
        <Plus size={13} /> Week
      </Button>
    </div>
  );
}

/* Setup of a new block: everything prefilled, so Start is one tap. */
function Setup({ t, form, setForm, choice, exercise, dayName }) {
  const tpl = templateOf(form.templateId);
  const rule = tpl ? tpl.rule : null;
  const weeks = tpl ? tpl.weeks : form.weeks || [];
  const needsTm = tpl ? tpl.usesTrainingMax : weeksUsePct(form.weeks);
  const set = patch => setForm(f => ({ ...f, ...patch }));
  const tmHint = form.templateId === 'smolov-jr'
    ? 'Smolov Jr uses your current 1RM'
    : form.e1rm ? `${form.tmPct}% of your e1RM ${fmtKg(form.e1rm)} (last 60 days)` : 'No recent e1RM: enter one';
  return (
    <div style={{ padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.45 }}>
        {tpl ? tpl.description : choice.blurb}
      </div>
      {!tpl && (
        <Row t={t} title="Name">
          <Input t={t} value={form.name} onChange={e => set({ name: e.target.value })} style={{ height: 40 }} />
        </Row>
      )}
      {needsTm && (
        <Row t={t} title="Training max">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DraftNumberInput t={t} ariaLabel="Training max" value={form.trainingMax} min={1} max={1000} allowEmpty suffix="kg"
              onChange={v => set({ trainingMax: v })} style={{ width: 104, flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: t.textFaint, lineHeight: 1.3 }}>{tmHint}</span>
          </div>
        </Row>
      )}
      {rule && (
        <Row t={t} title="Sets × reps">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <DraftNumberInput t={t} ariaLabel="Sets" integer min={1} max={20} value={form.sets} onChange={v => set({ sets: v })} style={{ width: 52 }} />
            <span style={{ color: t.textFaint, fontSize: 12 }}>×</span>
            {rule === 'linear' ? (
              <DraftNumberInput t={t} ariaLabel="Reps" integer min={1} max={100} value={form.reps} onChange={v => set({ reps: v })} style={{ width: 52 }} />
            ) : (
              <>
                <DraftNumberInput t={t} ariaLabel="Lowest reps" integer min={1} max={100} value={form.repMin} onChange={v => set({ repMin: v })} style={{ width: 52 }} />
                <span style={{ color: t.textFaint, fontSize: 12 }}>–</span>
                <DraftNumberInput t={t} ariaLabel="Highest reps" integer min={1} max={100} value={form.repMax} onChange={v => set({ repMax: v })} style={{ width: 52 }} />
              </>
            )}
          </div>
        </Row>
      )}
      {rule && (
        <Row t={t} title="Start weight">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DraftNumberInput t={t} ariaLabel="Start weight" value={form.startWeight} min={0.5} max={1000} allowEmpty suffix="kg" placeholder="auto"
              onChange={v => set({ startWeight: v })} style={{ width: 104, flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: t.textFaint, lineHeight: 1.3 }}>Empty: ~2 reps in reserve from your e1RM</span>
          </div>
        </Row>
      )}
      {(rule || needsTm) && (
        <Row t={t} title={rule ? 'Step' : 'TM per cycle'}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DraftNumberInput t={t} ariaLabel={rule ? 'Step per session' : 'Training max increase per cycle'} value={form.tmIncrement}
              min={0} max={50} suffix="kg" onChange={v => set({ tmIncrement: v })} style={{ width: 104, flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: t.textFaint, lineHeight: 1.3 }}>
              {rule ? 'added when every set is hit'
                : `added after each cycle${weeks.length > 1 ? ` of ${weeks.length} ${form.advance === 'calendar' ? 'weeks' : 'sessions'}` : ''}`}
            </span>
          </div>
        </Row>
      )}
      {!tpl && (
        <WeekEditor t={t} weeks={form.weeks} onChange={w => set({ weeks: w })} />
      )}
      <Row t={t} title="Starts">
        <input type="date" aria-label="Start date" value={form.startDate}
          onChange={e => e.target.value && set({ startDate: e.target.value })}
          style={{
            width: '100%', height: 40, background: t.inputBg, color: t.text, border: `1px solid ${t.inputBorder}`,
            borderRadius: 8, padding: '0 10px', fontFamily: FONT_MONO, fontSize: 13, outline: 'none', colorScheme: 'inherit',
          }} />
      </Row>
      {weeks.length > 1 && (
        <Row t={t} title="Start at">
          <select aria-label="Start at week" value={form.startWeek} onChange={e => set({ startWeek: Number(e.target.value) })}
            style={{
              width: '100%', height: 40, background: t.inputBg, color: t.text, border: `1px solid ${t.inputBorder}`,
              borderRadius: 8, padding: '0 10px', fontFamily: FONT_BODY, fontSize: 13, outline: 'none',
            }}>
            {weeks.map((w, i) => <option key={i} value={i}>{w.label || `Week ${i + 1}`}</option>)}
          </select>
        </Row>
      )}
      <Row t={t} title="Round to">
        <Segmented t={t} ariaLabel="Round loads to" value={form.roundTo} onChange={v => set({ roundTo: v })}
          options={ROUND_CHOICES.map(v => ({ value: v, label: fmtKg(v) }))} />
      </Row>
      {weeks.length > 1 && (
        <Row t={t} title="Advance">
          <Segmented t={t} ariaLabel="Advance" value={form.advance} onChange={v => set({ advance: v })}
            options={[{ value: 'session', label: 'Each session' }, { value: 'calendar', label: 'Each Monday' }]} />
        </Row>
      )}
      {form.canScope && (
        <Row t={t} title="Days">
          <Segmented t={t} ariaLabel="Plan days" value={form.scope} onChange={v => set({ scope: v })}
            options={[{ value: 'day', label: `${dayName || 'This day'} only` }, { value: 'all', label: 'Every day' }]} />
        </Row>
      )}
      <div style={{ fontSize: 11, color: t.textFaint, lineHeight: 1.4 }}>
        {exercise}: the running program ends the day before; your history stays one log.
      </div>
    </div>
  );
}

/**
 * The program of one exercise: the active block (week, training max, this
 * session's sets), switching to a template in ~3 taps (Program, template,
 * Start), ending it, and the blocks before.
 * @param {object} planExercise the plan exercise (for its progression), or null
 * @param {string} planDayId the plan day the sheet was opened from, or null
 * @param {string} date YYYY-MM-DD the targets are shown for
 */
export function ProgramSheet({ t, state, setState, exercise, planExercise, planDayId, date, onClose }) {
  // A tap that opened the sheet must not also close it through the backdrop.
  const openedAt = useRef(Date.now());
  const dismiss = () => { if (Date.now() - openedAt.current > 350) onClose(); };
  const [setup, setSetup] = useState(null); // { choice, form } while setting up a block
  const [tmDraft, setTmDraft] = useState(null); // number | null while editing the training max
  const [confirmEnd, setConfirmEnd] = useState(false);
  const ov = programOverview(state, exercise, date, planDayId);
  const dayName = (state.plan || []).find(d => d.id === planDayId)?.name || null;
  const choices = templateChoices();

  const choose = choice => setSetup({
    choice,
    form: setupDefaults(state, { exercise, date, templateId: choice.id, planExercise, planDayId }),
  });
  const setForm = fn => setSetup(s => ({ ...s, form: fn(s.form) }));
  const problem = setup ? setupProblem(setup.form) : null;
  const start = () => {
    const block = blockFromSetup(setup.form, { exercise, planDayId }, uid('prog'));
    setState(prev => startProgram(prev, block));
    onClose();
  };

  const active = ov.active;
  const now = ov.now;
  const activeTpl = active ? templateOf(active.template) : null;
  const showTm = active && (now?.trainingMax != null || (activeTpl ? activeTpl.usesTrainingMax : weeksUsePct(active.weeks)));
  const weekLine = now && (now.weekCount > 1
    ? `${now.weekLabel} · ${now.weekIndex + 1}/${now.weekCount}${now.cycle ? ` · cycle ${now.cycle + 1}` : ''}`
    : `${now.weekLabel} · session ${now.sessionsDone + 1}`);
  const rule = planExercise ? progressionOf(planExercise) : null;
  const step = planExercise ? loadStep(exercise, planExercise, state.logs, state.exerciseMeta) : null;

  return (
    <div onClick={dismiss} style={{
      position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }}>
      <div role="dialog" aria-label={`Program for ${exercise}`} onClick={e => e.stopPropagation()} style={{
        background: t.cardBg, borderTopLeftRadius: 16, borderTopRightRadius: 16, width: '100%', maxWidth: 560,
        maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 -8px 32px rgba(0,0,0,0.30)',
        paddingBottom: 'env(safe-area-inset-bottom)', fontFamily: FONT_BODY,
      }}>
        <div style={{ padding: '10px 8px 8px 8px', display: 'flex', alignItems: 'center', gap: 4, borderBottom: `1px solid ${t.divider}` }}>
          {setup ? (
            <button onClick={() => setSetup(null)} aria-label="Back" style={{ ...iconBtn(t), width: 44, height: 44, color: t.text }}>
              <ChevronLeft size={20} />
            </button>
          ) : (
            <span style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Layers size={18} color={t.accent} />
            </span>
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.text }}>
              {setup ? `Start ${setup.choice.name}` : 'Program'}
            </div>
            <div style={{ fontSize: 12, color: t.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{exercise}</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ ...iconBtn(t), width: 44, height: 44 }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain' }}>
          {setup ? (
            <Setup t={t} form={setup.form} setForm={setForm} choice={setup.choice} exercise={exercise} dayName={dayName} />
          ) : (
            <>
              {/* Now */}
              <div style={{ padding: '12px 18px 0' }}>
                {active ? (
                  <div style={{ padding: 12, borderRadius: 12, background: t.accentSoft, border: `1px solid ${t.accentBorder}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 16, fontWeight: 700, color: t.text }}>{active.name}</span>
                      <span style={{ fontSize: 11, color: t.textMuted }}>
                        since {shortDate(active.startDate)}{ov.activeStats?.days ? ` · ${ov.activeStats.days.join(', ')} only` : ''}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: t.text, fontFamily: FONT_MONO }}>{weekLine}</div>
                    {showTm && (tmDraft === null ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 12, color: t.textMuted }}>Training max</span>
                        <span style={{ fontSize: 14, fontFamily: FONT_MONO, fontWeight: 700, color: t.text }}>
                          {now?.trainingMax != null ? `${fmtKg(now.trainingMax)} kg` : 'not set'}
                        </span>
                        <Button t={t} size="sm" variant="ghost" style={{ height: 40, marginLeft: 'auto' }}
                          onClick={() => setTmDraft(now?.trainingMax ?? null)}>Edit</Button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <DraftNumberInput t={t} ariaLabel="New training max" value={tmDraft} min={1} max={1000} allowEmpty suffix="kg"
                          onChange={setTmDraft} style={{ width: 110 }} />
                        <Button t={t} size="sm" variant="primary" disabled={!(tmDraft > 0)} style={{ height: 40 }}
                          onClick={() => {
                            setState(prev => editTrainingMax(prev, active, tmDraft, date, uid('prog')));
                            setTmDraft(null);
                          }}>Save</Button>
                        <Button t={t} size="sm" variant="ghost" style={{ height: 40 }} onClick={() => setTmDraft(null)}>Cancel</Button>
                      </div>
                    ))}
                    {now && now.sets.length > 0 && (
                      <div>
                        <div style={{ ...label(t), marginBottom: 6 }}>This session</div>
                        <SetChips t={t} sets={now.sets} />
                      </div>
                    )}
                    {now?.note && <div style={{ fontSize: 12, color: t.textMuted }}>{now.note}</div>}
                    <div style={{ fontSize: 11, color: t.textMuted, fontFamily: FONT_MONO }}>{e1rmLine(ov.activeStats)}</div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      {confirmEnd ? (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <span style={{ fontSize: 12, color: t.textMuted }}>Back to the plan?</span>
                          <Button t={t} size="sm" variant="danger" style={{ height: 40 }}
                            onClick={() => { setState(prev => endProgram(prev, active, date)); setConfirmEnd(false); }}>
                            End program
                          </Button>
                          <Button t={t} size="sm" variant="ghost" style={{ height: 40 }} onClick={() => setConfirmEnd(false)}>Keep</Button>
                        </div>
                      ) : (
                        <Button t={t} size="sm" variant="ghost" style={{ height: 40, color: t.danger }} onClick={() => setConfirmEnd(true)}>
                          End program
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={{ padding: 12, borderRadius: 12, background: t.surfaceAlt, border: `1px solid ${t.cardBorder}` }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: t.text }}>No program</div>
                    <div style={{ fontSize: 12, color: t.textMuted, marginTop: 4, lineHeight: 1.45 }}>
                      {!planExercise ? 'Targets come from your last session.'
                        : rule === 'manual' ? 'Plan progression is manual: targets are your last session.'
                          : `Plan progression: ${RULE_LABEL[rule].toLowerCase()} ${repRangeLabel(planExercise)} reps, +${fmtKg(step)} kg steps.`}
                    </div>
                  </div>
                )}
              </div>

              {ov.upcoming.length > 0 && (
                <>
                  <SectionTitle t={t}>Coming up</SectionTitle>
                  {ov.upcoming.map(r => (
                    <div key={r.block.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 18px', minHeight: 48 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: t.text }}>{r.block.name}</div>
                        <div style={{ fontSize: 11, color: t.textMuted }}>starts {shortDate(r.block.startDate)}{r.days ? ` · ${r.days.join(', ')}` : ''}</div>
                      </div>
                      <Button t={t} size="sm" variant="ghost" style={{ height: 40 }} onClick={() => setState(prev => cancelUpcoming(prev, r.block))}>
                        Cancel
                      </Button>
                    </div>
                  ))}
                </>
              )}

              <SectionTitle t={t}>{active ? 'Switch program' : 'Start a program'}</SectionTitle>
              {choices.map(c => (
                <button key={c.id} onClick={() => choose(c)} style={{
                  width: '100%', minHeight: 56, padding: '8px 12px 8px 18px', display: 'flex', alignItems: 'center', gap: 10,
                  background: 'transparent', border: 'none', borderTop: `1px solid ${t.divider}`, cursor: 'pointer',
                  textAlign: 'left', fontFamily: FONT_BODY, touchAction: 'manipulation',
                }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, color: t.text }}>
                      {c.name}
                      {active && active.template === c.id && (
                        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', padding: '1px 6px', borderRadius: 4, background: t.accentSoft, color: t.accent }}>NOW</span>
                      )}
                    </span>
                    <span style={{ display: 'block', fontSize: 12, color: t.textMuted, marginTop: 2, lineHeight: 1.35 }}>{c.blurb}</span>
                  </span>
                  <ChevronRight size={16} color={t.textFaint} style={{ flexShrink: 0 }} />
                </button>
              ))}

              {ov.past.length > 0 && (
                <>
                  <SectionTitle t={t}>Earlier blocks</SectionTitle>
                  {ov.past.map(r => (
                    <div key={r.block.id} style={{ padding: '8px 18px', borderTop: `1px solid ${t.divider}` }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: t.text, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.block.name}
                        </span>
                        <span style={{ fontSize: 11, color: t.textMuted, flexShrink: 0 }}>
                          {shortDate(r.block.startDate)} – {shortDate(r.block.endDate)}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: t.textMuted, fontFamily: FONT_MONO, marginTop: 2 }}>
                        {e1rmLine(r)}{r.days ? ` · ${r.days.join(', ')}` : ''}
                      </div>
                    </div>
                  ))}
                </>
              )}
              <div style={{ height: 12 }} />
            </>
          )}
        </div>

        {setup && (
          <div style={{ padding: '10px 18px 14px', borderTop: `1px solid ${t.divider}` }}>
            {problem && <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 8 }}>{problem}</div>}
            <Button t={t} variant="primary" size="lg" disabled={!!problem} onClick={start}
              style={{ width: '100%', height: 48, justifyContent: 'center' }}>
              Start {setup.form.name || setup.choice.name}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
